<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Compact header with bulk actions -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto">
        <v-select
          v-model="selectedBulkAction"
          :items="bulkActions"
          item-title="text"
          item-value="value"
          placeholder="Выберите действие"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          :disabled="selectedItems.length === 0"
          @update:model-value="executeBulkAction"
          style="min-width: 200px"
        ></v-select>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Коды приглашений</h4>
      </v-col>
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <v-btn
            color="primary"
            variant="elevated"
            @click="openCreateDialog"
            prepend-icon="mdi-plus"
          >
            Создать код
          </v-btn>
          <!-- used codes removed with «Удалить» stay hidden; shown on demand -->
          <v-switch
            v-model="showHidden"
            label="Показать скрытые"
            color="primary"
            density="compact"
            hide-details
            @update:model-value="loadInvitations"
          ></v-switch>
          
          <!-- Pagination controls with total count -->
          <span class="text-caption">На странице:</span>
          <v-select
            v-model="limit"
            :items="[5, 10, 20, 50, 100]"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 160px"
            @update:model-value="onPageChange(1)"
          ></v-select>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            size="small"
            @update:model-value="onPageChange"
          ></v-pagination>
          <span class="text-caption font-weight-bold">Найдено: {{ totalItems }}</span>
        </div>
      </v-col>
    </v-row>

    <!-- Table section -->
    <v-card>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="invitations"
          :items-per-page="-1"
          :loading="isLoading"
          :sort-by="sortBy"
          @update:sort-by="updateSort"
          class="elevation-1"
          hover
          fixed-header
          height="calc(100vh - 236px)"
          hide-default-footer
        >
          <!-- Checkbox column -->
          <template v-slot:[`item.checkbox`]="{ item }">
            <v-checkbox
              v-model="selectedItems"
              :value="item.id"
              hide-details
              density="compact"
            ></v-checkbox>
          </template>

          <!-- Code column with copy button -->
          <template v-slot:[`item.code`]="{ item }">
            <div class="d-flex align-center">
              <v-chip
                :color="getCodeChipColor(item)"
                variant="tonal"
                size="small"
                class="mr-2"
              >
                {{ item.code }}
              </v-chip>
              <v-btn
                icon="mdi-content-copy"
                variant="text"
                size="x-small"
                @click="copyToClipboard(item.code)"
              ></v-btn>
            </div>
          </template>

          <!-- Status column -->
          <template v-slot:[`item.note`]="{ item }">
            <span class="text-body-2">{{ item.note || '—' }}</span>
          </template>

          <!-- Roles a user registered with the code gets at once -->
          <template v-slot:[`item.roles`]="{ item }">
            <v-chip v-for="name in item.role_names" :key="name" size="small" variant="tonal" color="deep-purple" class="mr-1">{{ name }}</v-chip>
            <span v-if="!item.role_names || !item.role_names.length" class="text-disabled">—</span>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusChipColor(item)"
              variant="tonal"
              size="small"
            >
              {{ getStatusText(item) }}
            </v-chip>
          </template>

          <!-- Usage column -->
          <template v-slot:[`item.usage`]="{ item }">
            <div class="text-center">
              <v-progress-linear
                :model-value="(item.current_uses / item.max_uses) * 100"
                :color="getUsageColor(item)"
                height="4"
                class="mb-1"
              ></v-progress-linear>
              <span class="text-caption">{{ item.current_uses }}/{{ item.max_uses }}</span>
            </div>
          </template>

          <!-- Dates -->
          <template v-slot:[`item.created_at`]="{ item }">
            <span class="text-caption">{{ formatDate(item.created_at) }}</span>
          </template>

          <template v-slot:[`item.expires_at`]="{ item }">
            <span class="text-caption" :class="{ 'text-error': isExpired(item) }">
              {{ item.expires_at ? formatDate(item.expires_at) : 'Без ограничений' }}
            </span>
          </template>

          <!-- Users column -->
          <template v-slot:[`item.users`]="{ item }">
            <v-btn
              v-if="item.usage_records && item.usage_records.length > 0"
              icon="mdi-account-multiple"
              variant="text"
              size="small"
              color="info"
              @click="showUsageDetails(item)"
            ></v-btn>
            <span v-else class="text-caption text-disabled">—</span>
          </template>

          <!-- Actions column -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex">
              <v-btn
                icon="mdi-pencil"
                variant="text"
                size="small"
                @click="editInvitation(item)"
                :disabled="!item.is_active"
              ></v-btn>
              <v-btn
                :icon="item.is_active ? 'mdi-pause' : 'mdi-play'"
                variant="text"
                size="small"
                @click="toggleInvitationStatus(item)"
                :color="item.is_active ? 'warning' : 'success'"
              ></v-btn>
              <v-btn
                icon="mdi-delete"
                variant="text"
                size="small"
                color="error"
                @click="deleteInvitation(item)"
              ></v-btn>
            </div>
          </template>

        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">{{ isEditing ? 'Редактировать код' : 'Создать код приглашения' }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeDialog"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <v-form ref="invitationForm" v-model="formValid">
            <v-text-field
              v-model="formData.custom_code"
              :disabled="isEditing"
              label="Пользовательский код (необязательно)"
              variant="outlined"
              :rules="customCodeRules"
              hint="Если не указан, будет сгенерирован автоматически"
              persistent-hint
              class="mb-4"
            ></v-text-field>

            <v-text-field
              v-model="formData.max_uses"
              :disabled="isEditing"
              label="Максимальное количество использований"
              variant="outlined"
              type="number"
              :rules="maxUsesRules"
              min="1"
              class="mb-4"
            ></v-text-field>

            <v-text-field
              v-model="formData.expires_in_days"
              v-if="!isEditing"
              label="Действителен (дней)"
              variant="outlined"
              type="number"
              :rules="expiresRules"
              min="1"
              hint="Оставьте пустым для бессрочного кода"
              persistent-hint
              class="mb-4"
            ></v-text-field>

            <!-- the expiry of an existing code: a date (the code works to the end of that day) or none -->
            <div v-if="isEditing" class="d-flex align-center mb-4">
              <v-text-field
                v-model="formData.expires_date"
                :disabled="formData.no_expiry"
                label="Действует до"
                type="date"
                variant="outlined"
                :min="today"
                :rules="expiresDateRules"
                hide-details="auto"
                class="mr-4"
              ></v-text-field>
              <v-checkbox
                v-model="formData.no_expiry"
                label="Бессрочно"
                density="compact"
                hide-details
              ></v-checkbox>
            </div>

            <v-textarea
              v-model="formData.note"
              label="Примечание"
              placeholder="Для кого код: имя, организация, почта"
              variant="outlined"
              rows="2"
              auto-grow
              counter="500"
              :rules="noteRules"
              class="mb-4"
            ></v-textarea>

            <v-select
              v-model="formData.role_ids"
              :items="roleItems"
              item-title="title"
              item-value="id"
              label="Роли после регистрации"
              hint="Назначаются сразу, вместе с ролью user. Роль admin кодом не выдаётся"
              persistent-hint
              variant="outlined"
              multiple
              chips
              closable-chips
              class="mb-4"
            ></v-select>

            <v-select
              v-model="formData.plan_id"
              :items="planItems"
              item-title="title"
              item-value="id"
              label="Тариф после регистрации"
              variant="outlined"
            ></v-select>

            <!-- the team a user registered with the code joins (task 59) -->
            <v-select
              v-model="formData.team_id"
              :items="teamItems"
              item-title="title"
              item-value="id"
              label="Команда после регистрации"
              variant="outlined"
            ></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeDialog"
            :disabled="isSubmitting"
          >
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            @click="saveInvitation"
            :loading="isSubmitting"
            :disabled="!formValid"
          >
            {{ isEditing ? 'Сохранить' : 'Создать' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Usage Details Dialog -->
    <v-dialog v-model="showUsageDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Использования кода {{ usageDialogInvitation?.code }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeUsageDialog"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            v-if="usageDialogInvitation?.usage_records?.length > 0"
            :headers="usageHeaders"
            :items="usageTableItems"
            :items-per-page="5"
            class="elevation-1"
          >
            <!-- User column -->
            <template v-slot:[`item.user`]="{ item }">
              <div class="d-flex align-center">
                <v-avatar size="32" class="mr-2">
                  <v-icon>mdi-account-circle</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-medium">{{ item.user_name || 'Загрузка...' }}</div>
                  <div class="text-caption text-disabled">{{ item.user_email || 'Загрузка...' }}</div>
                </div>
              </div>
            </template>
            
            <!-- Used at column -->
            <template v-slot:[`item.used_at`]="{ item }">
              <span class="text-caption">{{ formatDate(item.used_at) }}</span>
            </template>
          </v-data-table>
          
          <div v-else class="text-center py-4">
            <v-icon size="48" color="disabled">mdi-account-off</v-icon>
            <div class="text-h6 mt-2">Код еще не использовался</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="closeUsageDialog"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="showSuccessSnackbar"
      color="success"
      timeout="3000"
      location="top"
    >
      {{ successMessage }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showSuccessSnackbar = false"
        >
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showErrorSnackbar"
      color="error"
      timeout="5000"
      location="top"
    >
      {{ errorMessage }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showErrorSnackbar = false"
        >
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'

const EMPTY_FORM = { custom_code: '', max_uses: 1, expires_in_days: 7, note: '', plan_id: null, team_id: null }

export default {
  name: 'InvitationCodes',
  data() {
    return {
      // Table state
      invitations: [],
      isLoading: false,
      selectedItems: [],
      headers: [
        { title: '', key: 'checkbox', sortable: false, width: '40px' },
        { title: 'Код', key: 'code', sortable: true },
        { title: 'Примечание', key: 'note', sortable: false },
        { title: 'Роли', key: 'roles', sortable: false },
        { title: 'Статус', key: 'status', sortable: true },
        { title: 'Использование', key: 'usage', sortable: false, width: '120px' },
        { title: 'Пользователи', key: 'users', sortable: false, width: '60px' },
        { title: 'Создан', key: 'created_at', sortable: true },
        { title: 'Истекает', key: 'expires_at', sortable: true },
        { title: 'Действия', key: 'actions', sortable: false, width: '140px' }
      ],

      // Pagination
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      limit: 20,
      sortBy: [{ key: 'created_at', order: 'desc' }],

      // Bulk actions
      selectedBulkAction: null,
      bulkActions: [
        { text: 'Деактивировать выбранные', value: 'deactivate' },
        { text: 'Активировать выбранные', value: 'activate' },
        { text: 'Удалить выбранные', value: 'delete' }
      ],

      // Dialog state
      showDialog: false,
      isEditing: false,
      isSubmitting: false,
      formValid: false,
      formData: { ...EMPTY_FORM, role_ids: [] },
      showHidden: false,
      plans: [],
      teams: [],
      roles: [],
      editingInvitation: null,

      // Form validation rules
      noteRules: [
        v => !v || v.length <= 500 || 'Не больше 500 символов'
      ],
      customCodeRules: [
        v => !v || v.length >= 12 || 'Код должен содержать минимум 12 символов',
        v => !v || /^[a-zA-Z0-9_-]+$/.test(v) || 'Код может содержать только буквы, цифры, дефисы и подчеркивания'
      ],
      maxUsesRules: [
        v => !!v || 'Поле обязательно',
        v => parseInt(v) > 0 || 'Должно быть больше 0'
      ],
      expiresRules: [
        v => !v || parseInt(v) > 0 || 'Должно быть больше 0'
      ],
      expiresDateRules: [
        v => this.formData.no_expiry || !!v || 'Укажите дату или отметьте «Бессрочно»',
        v => this.formData.no_expiry || !v || v >= this.today || 'Дата не может быть в прошлом'
      ],

      // Snackbars
      showSuccessSnackbar: false,
      showErrorSnackbar: false,
      successMessage: '',
      errorMessage: '',

      // Usage details modal
      showUsageDialog: false,
      usageDialogInvitation: null,
      usageHeaders: [
        { title: 'Пользователь', key: 'user', sortable: false },
        { title: 'Дата использования', key: 'used_at', sortable: true }
      ]
    }
  },
  watch: {
    // the rule of the date depends on the checkbox: check the form again when it changes
    'formData.no_expiry'() {
      this.$nextTick(() => this.$refs.invitationForm?.validate())
    }
  },
  computed: {
    today() {
      return this.localDate(new Date())
    },
    planItems() {
      const def = this.plans.find(p => p.is_default)
      return [{ id: null, title: def ? `${def.name} (по умолчанию)` : 'По умолчанию' },
        ...this.plans.filter(p => !p.is_default).map(p => ({ id: p.id, title: p.name }))]
    },
    teamItems() {
      return [{ id: null, title: 'Без команды' }, ...this.teams.map(t => ({ id: t.id, title: t.name }))]
    },
    // roles a code may give: all but admin
    roleItems() {
      return this.roles.filter(r => r.name !== 'admin')
        .map(r => ({ id: r.id, title: r.description ? `${r.name} — ${r.description}` : r.name }))
    },
    offset() {
      return (this.currentPage - 1) * this.limit
    },
    usageTableItems() {
      if (!this.usageDialogInvitation?.usage_records) return []
      
      return this.usageDialogInvitation.usage_records.map(usage => {
        const displayName = usage.username || `Пользователь ${usage.user_id}`
        
        return {
          user_id: usage.user_id,
          used_at: usage.used_at,
          user_name: displayName,
          user_email: usage.user_email || 'Не указан'
        }
      })
    }
  },
  async mounted() {
    await this.loadInvitations()
    this.loadRoles()
    // Add ESC key listener
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    // Remove ESC key listener
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    // Data loading
    async loadInvitations() {
      this.isLoading = true
      try {
        const params = {
          limit: this.limit,
          offset: this.offset,
          include_inactive: true,
          include_hidden: this.showHidden
        }
        
        const response = await apiClient.get('/admin/invitations', { params })
        this.invitations = response.data.invitations
        this.totalItems = response.data.total
        this.totalPages = Math.ceil(this.totalItems / this.limit)
        
      } catch (error) {
        this.showError('Ошибка загрузки кодов приглашений: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.isLoading = false
      }
    },

    async loadRoles() {
      try {
        this.plans = (await apiClient.get('/admin/plans')).data.items || []
      } catch (error) {
        this.plans = []
      }
      try {
        this.teams = (await apiClient.get('/admin/teams')).data.items || []
      } catch (error) {
        this.teams = []
      }
      try {
        this.roles = (await apiClient.get('/admin/roles')).data.roles || []
      } catch (error) {
        this.showError('Ошибка загрузки ролей: ' + (error.response?.data?.detail || error.message))
      }
    },

    // Pagination
    async onPageChange(page) {
      this.currentPage = page
      await this.loadInvitations()
    },

    // Sorting
    async updateSort(sortBy) {
      this.sortBy = sortBy
      await this.loadInvitations()
    },

    // CRUD Operations
    openCreateDialog() {
      this.isEditing = false
      this.editingInvitation = null
      this.formData = { ...EMPTY_FORM, role_ids: [] }
      this.showDialog = true
    },

    editInvitation(invitation) {
      this.isEditing = true
      this.editingInvitation = invitation
      // code and uses stay; note, roles, plan, team and expiry change
      this.formData = {
        custom_code: invitation.code,
        max_uses: invitation.max_uses,
        expires_in_days: null,
        expires_date: invitation.expires_at ? this.localDate(new Date(invitation.expires_at)) : '',
        no_expiry: !invitation.expires_at,
        note: invitation.note || '',
        role_ids: [...(invitation.role_ids || [])],
        plan_id: invitation.plan_id ?? null,
        team_id: invitation.team_id ?? null
      }
      this.showDialog = true
    },

    async saveInvitation() {
      if (!this.formValid) return
      
      this.isSubmitting = true
      try {
        if (this.isEditing) {
          await apiClient.patch(`/admin/invitations/${this.editingInvitation.id}`, {
            note: this.formData.note || '',
            role_ids: this.formData.role_ids,
            plan_id: this.formData.plan_id,
            team_id: this.formData.team_id,
            expires_at: this.formData.no_expiry ? null : new Date(`${this.formData.expires_date}T23:59:59`).toISOString()
          })
          this.showSuccess('Код приглашения обновлён')
          await this.loadInvitations()
        } else {
          const data = {
            custom_code: this.formData.custom_code || null,
            max_uses: parseInt(this.formData.max_uses),
            expires_in_days: this.formData.expires_in_days ? parseInt(this.formData.expires_in_days) : null,
            note: this.formData.note || null,
            role_ids: this.formData.role_ids,
            plan_id: this.formData.plan_id,
            team_id: this.formData.team_id
          }
          
          await apiClient.post('/admin/invitations', data)
          this.showSuccess('Код приглашения успешно создан')
          await this.loadInvitations()
        }
        this.closeDialog()
      } catch (error) {
        this.showError('Ошибка сохранения: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.isSubmitting = false
      }
    },

    async toggleInvitationStatus(invitation) {
      try {
        if (invitation.is_active) {
          await apiClient.post('/admin/invitations/revoke', {
            invitation_id: invitation.id
          })
          this.showSuccess('Код деактивирован')
        } else {
          // API doesn't support reactivation, but we show the message
          this.showError('Повторная активация не поддерживается')
        }
        await this.loadInvitations()
      } catch (error) {
        this.showError('Ошибка изменения статуса: ' + (error.response?.data?.detail || error.message))
      }
    },

    async deleteInvitation(invitation) {
      if (!confirm(`Удалить код приглашения "${invitation.code}"?`)) return
      
      try {
        // unused: deleted; used: deactivated and hidden (the answer says which)
        const resp = await apiClient.delete(`/admin/invitations/${invitation.id}`)
        this.showSuccess(resp.data.message)
        await this.loadInvitations()
      } catch (error) {
        this.showError('Ошибка удаления: ' + (error.response?.data?.detail || error.message))
      }
    },

    // Bulk operations
    async executeBulkAction() {
      if (!this.selectedBulkAction || this.selectedItems.length === 0) return
      
      const actionText = this.bulkActions.find(a => a.value === this.selectedBulkAction)?.text
      if (!confirm(`${actionText} для ${this.selectedItems.length} кодов?`)) return
      
      try {
        for (const invitationId of this.selectedItems) {
          if (this.selectedBulkAction === 'deactivate') {
            await apiClient.post('/admin/invitations/revoke', {
              invitation_id: invitationId
            })
          } else if (this.selectedBulkAction === 'delete') {
            await apiClient.delete(`/admin/invitations/${invitationId}`)
          }
        }
        
        this.showSuccess(`Выполнено: ${actionText}`)
        this.selectedItems = []
        this.selectedBulkAction = null
        await this.loadInvitations()
        
      } catch (error) {
        this.showError('Ошибка выполнения операции: ' + (error.response?.data?.detail || error.message))
      }
    },

    // Dialog management
    closeDialog() {
      this.showDialog = false
      this.isEditing = false
      this.editingInvitation = null
      this.formData = { ...EMPTY_FORM, role_ids: [] }
      if (this.$refs.invitationForm) {
        this.$refs.invitationForm.resetValidation()
      }
    },

    // Utility methods
    getCodeChipColor(invitation) {
      if (!invitation.is_active) return 'error'
      if (this.isExpired(invitation)) return 'warning'
      if (invitation.current_uses >= invitation.max_uses) return 'info'
      return 'success'
    },

    getStatusChipColor(invitation) {
      if (!invitation.is_active) return 'error'
      if (this.isExpired(invitation)) return 'warning'
      if (invitation.current_uses >= invitation.max_uses) return 'info'
      return 'success'
    },

    getStatusText(invitation) {
      if (invitation.hidden_at) return 'Скрыт'
      if (!invitation.is_active) return 'Неактивен'
      if (this.isExpired(invitation)) return 'Истек'
      if (invitation.current_uses >= invitation.max_uses) return 'Исчерпан'
      return 'Активен'
    },

    getUsageColor(invitation) {
      const percentage = (invitation.current_uses / invitation.max_uses) * 100
      if (percentage === 100) return 'error'
      if (percentage >= 80) return 'warning'
      return 'success'
    },

    isExpired(invitation) {
      if (!invitation.expires_at) return false
      return new Date(invitation.expires_at) < new Date()
    },

    localDate(d) {
      // YYYY-MM-DD in the time zone of the browser, as the date field shows it
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU') + ' ' + date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.showSuccess('Код скопирован в буфер обмена')
      } catch (error) {
        this.showError('Ошибка копирования в буфер обмена')
      }
    },

    // Message helpers
    showSuccess(message) {
      this.successMessage = message
      this.showSuccessSnackbar = true
    },

    showError(message) {
      this.errorMessage = message
      this.showErrorSnackbar = true
    },

    // Usage details modal methods
    showUsageDetails(invitation) {
      this.usageDialogInvitation = invitation
      this.showUsageDialog = true
    },

    closeUsageDialog() {
      this.showUsageDialog = false
      this.usageDialogInvitation = null
    },

    // Keyboard event handlers
    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.showUsageDialog) {
          this.closeUsageDialog()
        } else if (this.showDialog) {
          this.closeDialog()
        }
      }
    }
  }
}
</script>